import React, { useState } from 'react';
import {
  Sparkles, X, Bot, ShieldCheck, Zap, Lock, MessageSquare,
  ArrowRight, Bell, CheckCircle2, ChevronRight, RefreshCw, Smartphone
} from 'lucide-react';

export default function MobileChatAssistantModal({ isOpen, onClose }) {
  const [notified, setNotified] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  if (!isOpen) return null;

  const SAMPLE_PROMPTS = [
    { text: 'Check if any of my passwords were leaked in recent breaches', tag: 'Security Audit' },
    { text: 'Find all payment cards expiring before December 2026', tag: 'Cards' },
    { text: 'Summarize my cloud SaaS recurring expenses for this month', tag: 'Expenses' },
    { text: 'Extract passport details from my uploaded PDF document', tag: 'Documents OCR' },
  ];

  return (
    <div className="mobile-chat-overlay animate-fade-in" role="dialog" aria-modal="true">
      <div className="mobile-chat-sheet animate-slide-up glass-panel">
        {/* iOS Handle */}
        <div className="mobile-sheet-pull-handle" />

        {/* Header */}
        <div className="mobile-chat-header">
          <div className="chat-avatar-cluster">
            <div className="chat-avatar-icon-box">
              <Sparkles size={20} className="text-accent animate-pulse" />
            </div>
            <div>
              <div className="chat-header-title-row">
                <h3>CipherAI Assistant</h3>
                <span className="chat-badge-preview">v2.5 Preview</span>
              </div>
              <p className="chat-header-sub">Zero-Knowledge Vault Intelligence Copilot</p>
            </div>
          </div>
          <button
            type="button"
            className="mobile-explore-clear-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        {/* Body */}
        <div className="mobile-chat-body">
          {/* Hero Card */}
          <div className="mobile-chat-hero-banner">
            <div className="chat-hero-glow" />
            <div className="chat-hero-content">
              <div className="chat-sparkle-pill">
                <Lock size={12} />
                <span>Client-Side End-to-End Encrypted</span>
              </div>
              <h4>Next-Generation AI Security for Your Digital Vault</h4>
              <p>
                CipherAI runs private, local-first inference to inspect breach logs, automate renewals, and answer vault questions without ever revealing your plaintext keys to third parties.
              </p>
            </div>
          </div>

          {/* Interactive Feature Teasers */}
          <div className="mobile-chat-features-list">
            <div className="chat-feature-item">
              <div className="feature-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                <ShieldCheck size={18} />
              </div>
              <div className="feature-info">
                <h5>Proactive Leak & Exposure Alerts</h5>
                <p>Monitors global darknet dumps and notifies you instantly if your email or passwords appear.</p>
              </div>
            </div>

            <div className="chat-feature-item">
              <div className="feature-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                <Zap size={18} />
              </div>
              <div className="feature-info">
                <h5>Smart Expense & Invoice Parser</h5>
                <p>Upload a photo or receipt to auto-create categorized transactions and tag card payment methods.</p>
              </div>
            </div>

            <div className="chat-feature-item">
              <div className="feature-icon-badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                <Bot size={18} />
              </div>
              <div className="feature-info">
                <h5>Conversational Vault Queries</h5>
                <p>Natural language voice & text commands right from your mobile navigation dock.</p>
              </div>
            </div>
          </div>

          {/* Suggested Prompts Grid */}
          <div className="mobile-chat-prompts-section">
            <div className="prompts-label">Try asking when live:</div>
            <div className="prompts-grid">
              {SAMPLE_PROMPTS.map((p, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`chat-prompt-card ${selectedPrompt === idx ? 'selected' : ''}`}
                  onClick={() => setSelectedPrompt(idx)}
                >
                  <span className="prompt-tag">#{p.tag}</span>
                  <p className="prompt-text">"{p.text}"</p>
                  {selectedPrompt === idx && (
                    <span className="prompt-preview-note">✓ Added to your early access wishlist</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer with Notification Opt-In */}
        <div className="mobile-chat-footer">
          <button
            type="button"
            className={`btn btn-block ${notified ? 'btn-success' : 'btn-primary'} mobile-chat-action-btn`}
            onClick={() => {
              if (navigator.vibrate) navigator.vibrate([40, 20, 40]);
              setNotified(true);
            }}
          >
            {notified ? (
              <>
                <CheckCircle2 size={16} />
                <span>You're on the VIP Beta List!</span>
              </>
            ) : (
              <>
                <Bell size={16} />
                <span>Notify Me When CipherAI Launches</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
